import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { useAuth } from '@/App';
import DashboardLayout from '@/components/DashboardLayout';
import SaaSOnboardingWizard from '@/components/SaaSOnboardingWizard';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuTrigger 
} from '@/components/ui/dropdown-menu';
import { Plus, Calendar, DollarSign, Save, MoreVertical, Archive, Trash2, CheckCircle, User, ArrowRight } from 'lucide-react';
import { toast } from '@/components/ui/sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PIPELINE_STAGES = [
  { key: 'inquiry', label: 'Inquiry', icon: '❓' },
  { key: 'follow_up', label: 'Follow-up', icon: '📞' },
  { key: 'proposal_sent', label: 'Proposal Sent', icon: '📄' },
  { key: 'contract_signed', label: 'Contract Signed', icon: '✍️' },
  { key: 'initiation', label: 'Initiation', icon: '🎯' },
  { key: 'planning', label: 'Planning', icon: '📋' },
  { key: 'execution', label: 'Execution', icon: '🚀' },
  { key: 'monitoring', label: 'Monitoring + Control', icon: '📊' },
  { key: 'closure', label: 'Closure', icon: '✅' }
];

const ProjectsPage = () => {
  const { token, user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showWizard, setShowWizard] = useState(false);
  const [resumeDraft, setResumeDraft] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [projectsRes, clientsRes, draftRes] = await Promise.all([
        axios.get(`${API}/projects`, { headers }),
        axios.get(`${API}/clients`, { headers }),
        axios.get(`${API}/project-drafts/my-draft`, { headers }).catch(() => ({ data: null }))
      ]);
      setProjects(projectsRes.data);
      setClients(clientsRes.data);
      setDraft(draftRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (formData) => {
    try {
      // Use atomic endpoint that creates client + project together
      // This prevents orphaned clients when project creation fails
      const wizardPayload = {
        client_name: formData.business_name?.trim() || `${formData.first_name} ${formData.last_name}`.trim(),
        client_email: formData.email?.toLowerCase().trim(),
        client_phone: formData.phone_number || '',
        client_company: formData.business_name || '',
        discovery_source: formData.discovery_source || '',
        project_name: formData.project_name?.trim() || 
          `${formData.business_name?.trim() || formData.first_name} - ${formData.setup_type?.charAt(0).toUpperCase() + formData.setup_type?.slice(1)} Platform`,
        setup_type: formData.setup_type,
        target_launch_date: formData.target_launch_date,
        wizard_data: formData
      };
      
      await axios.post(`${API}/projects/from-wizard`, wizardPayload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (draft) {
        await axios.delete(`${API}/project-drafts/${draft.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setShowWizard(false);
      setResumeDraft(false);
      fetchData();
      toast.success('Project created successfully!');
    } catch (error) {
      console.error('Failed to create project:', error);
      
      const errorDetail = error.response?.data?.detail || '';
      
      // Check for email not registered error (affects admins)
      if (error.response?.status === 400 && errorDetail.includes('not found. User must register')) {
        toast.error(errorDetail, { duration: 6000 });
      }
      // Check for email mismatch error (non-admin users only)
      else if (error.response?.status === 403 && errorDetail.includes('registered email')) {
        toast.error(`Email address must match your account email (${user?.email}). Please update to continue.`, {
          duration: 6000
        });
      } else {
        toast.error(errorDetail || 'Failed to create project. Please try again.');
      }
    }
  };

  const handleSaveDraft = async (formData, currentStep) => {
    try {
      await axios.post(`${API}/project-drafts`, {
        draft_data: formData,
        current_step: currentStep
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Draft saved successfully! You can continue later.');
      fetchData();
    } catch (error) {
      console.error('Failed to save draft:', error);
      toast.error('Failed to save draft. Please try again.');
    }
  };

  const handleResumeDraft = () => {
    setResumeDraft(true);
    setShowWizard(true);
  };

  const handleDeleteDraft = async () => {
    if (!window.confirm('Are you sure you want to delete this draft?')) return;
    
    try {
      await axios.delete(`${API}/project-drafts/${draft.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDraft(null);
    } catch (error) {
      console.error('Failed to delete draft:', error);
      alert('Failed to delete draft. Please try again.');
    }
  };

  const getStatusClass = (status) => {
    const statusMap = {
      inquiry: 'status-initiation',
      follow_up: 'status-initiation',
      proposal_sent: 'status-planning',
      contract_signed: 'status-planning',
      initiation: 'status-initiation',
      planning: 'status-planning',
      execution: 'status-execution',
      monitoring: 'status-monitoring',
      closure: 'status-closure',
      active: 'status-active',
      completed: 'status-completed',
      on_hold: 'status-on-hold'
    };
    return statusMap[status] || 'status-inactive';
  };

  const formatStatus = (status) => {
    const statusLabels = {
      inquiry: '❓ Inquiry',
      follow_up: '📞 Follow-up',
      proposal_sent: '📄 Proposal Sent',
      contract_signed: '✍️ Contract Signed',
      initiation: '🎯 Initiation',
      planning: '📋 Planning',
      execution: '🚀 Execution',
      monitoring: '📊 Monitoring',
      closure: '✅ Closure',
      active: '🟢 Active',
      completed: '✨ Completed',
      on_hold: '⏸️ On Hold'
    };
    return statusLabels[status] || status;
  };

  const handleArchive = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Archive this project? It will be moved to the Archive page.')) return;
    
    setActionLoading(projectId);
    try {
      await axios.post(`${API}/projects/${projectId}/archive`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      fetchData();
    } catch (error) {
      console.error('Failed to archive project:', error);
      alert('Failed to archive project. Please try again.');
    } finally {
      setActionLoading(null);
    }
  };

  const handleComplete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Mark this project as complete? It will be moved to the Completed section in Archive.')) return;
    
    setActionLoading(projectId);
    try {
      await axios.post(`${API}/projects/${projectId}/complete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project marked as completed');
      fetchData();
    } catch (error) {
      console.error('Failed to complete project:', error);
      toast.error('Failed to complete project');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (e, projectId) => {
    e.preventDefault();
    e.stopPropagation();
    if (!window.confirm('Delete this project? It will be moved to Trash and can be recovered from the Archive page.')) return;
    
    setActionLoading(projectId);
    try {
      await axios.post(`${API}/projects/${projectId}/soft-delete`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Project moved to Trash');
      fetchData();
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error('Failed to delete project');
    } finally {
      setActionLoading(null);
    }
  };

  const handleMoveToStage = async (e, projectId, newStatus) => {
    e.preventDefault();
    e.stopPropagation();
    setActionLoading(projectId);
    try {
      await axios.patch(`${API}/projects/${projectId}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Project moved to ${PIPELINE_STAGES.find(s => s.key === newStatus)?.label || newStatus}`);
      fetchData();
    } catch (error) {
      console.error('Failed to update project status:', error);
      toast.error('Failed to move project');
    } finally {
      setActionLoading(null);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64" data-testid="projects-loading">
          <div className="text-xl text-slate-gray">Loading...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 sm:space-y-8" data-testid="projects-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-carbon-black mb-1 sm:mb-2" data-testid="projects-title">Projects</h1>
            <p className="text-sm sm:text-base text-slate-gray">Manage your ongoing projects</p>
          </div>
          <Button 
            className="gradient-fearless text-white w-full sm:w-auto" 
            onClick={() => setShowWizard(true)}
            data-testid="add-project-btn"
          >
            <Plus className="mr-2 h-4 w-4 sm:h-5 sm:w-5" />
            Add Project
          </Button>
        </div>

        {/* Draft Banner */}
        {draft && !showWizard && (
          <Card className="shadow-lg rounded-xl border-2 border-yellow-400 bg-yellow-50" data-testid="draft-banner">
            <CardContent className="p-4 sm:p-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div className="flex items-start sm:items-center space-x-3 sm:space-x-4">
                  <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-yellow-400 flex items-center justify-center flex-shrink-0">
                    <Save className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                  </div>
                  <div className="min-w-0">
                    <h3 className="text-base sm:text-lg font-bold text-carbon-black mb-1">Draft Saved</h3>
                    <p className="text-xs sm:text-sm text-slate-gray">
                      Unfinished setup from {new Date(draft.updated_at).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-slate-gray mt-1 truncate">
                      Step {draft.current_step + 1}/8 • {draft.draft_data?.business_name || 'Unnamed'}
                    </p>
                  </div>
                </div>
                <div className="flex space-x-2 sm:space-x-3">
                  <Button
                    onClick={handleResumeDraft}
                    className="gradient-fearless text-white flex-1 sm:flex-none text-sm"
                    data-testid="resume-draft-btn"
                  >
                    Continue
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleDeleteDraft}
                    className="border-red-300 text-red-600 hover:bg-red-50 flex-1 sm:flex-none text-sm"
                    data-testid="delete-draft-btn"
                  >
                    Delete
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {showWizard && (
          <SaaSOnboardingWizard
            onComplete={handleSubmit}
            onCancel={() => {
              setShowWizard(false);
              setResumeDraft(false);
            }}
            onSaveDraft={handleSaveDraft}
            initialData={resumeDraft && draft ? draft.draft_data : null}
            initialStep={resumeDraft && draft ? draft.current_step : 0}
          />
        )}

        {projects.length === 0 ? (
          <Card className="shadow-lg rounded-xl border-0">
            <CardContent className="p-8 sm:p-12 text-center">
              <p className="text-slate-gray text-sm sm:text-lg">No projects yet. Create your first project to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:gap-6">
            {projects.map((project) => (
              <Link key={project.id} to={`/dashboard/projects/${project.id}`}>
                <Card className="shadow-lg rounded-xl border-0 card-hover" data-testid={`project-card-${project.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    {/* Mobile Layout */}
                    <div className="sm:hidden">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1 min-w-0 pr-2">
                          {project.project_number && (
                            <span className="inline-block px-2 py-0.5 rounded-full bg-fearless-blue/10 text-fearless-blue text-xs font-mono font-semibold mb-1" data-testid={`project-number-mobile-${project.id}`}>
                              {project.project_number}
                            </span>
                          )}
                          <h3 className="text-lg font-bold text-carbon-black line-clamp-2">{project.title}</h3>
                        </div>
                        <div className="flex items-center space-x-2 flex-shrink-0">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${getStatusClass(project.status)}`}>
                            {formatStatus(project.status)}
                          </span>
                          {user?.role === 'admin' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => e.preventDefault()}
                                  disabled={actionLoading === project.id}
                                  data-testid={`project-menu-${project.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {/* Move to Stage - inline for mobile */}
                                <DropdownMenuLabel className="text-xs text-slate-gray font-medium">
                                  Move to Stage
                                </DropdownMenuLabel>
                                <div className="max-h-[200px] overflow-y-auto">
                                  {PIPELINE_STAGES.map((stage) => (
                                    <DropdownMenuItem 
                                      key={stage.key}
                                      onClick={(e) => handleMoveToStage(e, project.id, stage.key)}
                                      disabled={project.status === stage.key}
                                      className={project.status === stage.key ? 'bg-gray-100' : ''}
                                    >
                                      <span className="mr-2">{stage.icon}</span>
                                      {stage.label}
                                      {project.status === stage.key && <span className="ml-auto text-xs text-fearless-blue">✓</span>}
                                    </DropdownMenuItem>
                                  ))}
                                </div>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={(e) => handleComplete(e, project.id)}
                                  className="cursor-pointer"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                  <span>Completed</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleArchive(e, project.id)}
                                  className="cursor-pointer"
                                >
                                  <Archive className="h-4 w-4 mr-2 text-blue-600" />
                                  <span>Archive</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleDelete(e, project.id)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                      <p className="text-sm text-slate-gray mb-3 line-clamp-2">{project.description}</p>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div className="flex items-center text-slate-gray">
                          <DollarSign className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="font-semibold">{project.budget.toLocaleString()}</span>
                        </div>
                        {project.start_date && (
                          <div className="flex items-center text-slate-gray">
                            <Calendar className="h-3 w-3 mr-1 flex-shrink-0" />
                            <span className="truncate">{project.start_date}</span>
                          </div>
                        )}
                        <div className="flex items-center text-slate-gray col-span-2">
                          <User className="h-3 w-3 mr-1 flex-shrink-0" />
                          <span className="truncate">Client: <span className="font-semibold">{project.client_name}</span></span>
                        </div>
                        {(project.milestone_count > 0 || project.task_count > 0) && (
                          <div className="text-slate-gray col-span-2">
                            {project.milestone_count || 0} milestones • {project.task_count || 0} tasks
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Desktop Layout */}
                    <div className="hidden sm:block">
                      <div className="flex items-start justify-between">
                        <div className="flex-1 min-w-0 pr-4">
                          {project.project_number && (
                            <span className="inline-block px-2.5 py-1 rounded-full bg-fearless-blue/10 text-fearless-blue text-xs font-mono font-semibold mb-2" data-testid={`project-number-desktop-${project.id}`}>
                              {project.project_number}
                            </span>
                          )}
                          <h3 className="text-xl lg:text-2xl font-bold text-carbon-black mb-2 truncate">{project.title}</h3>
                          <p className="text-slate-gray mb-4 line-clamp-2">{project.description}</p>
                          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
                            <div className="flex items-center text-slate-gray">
                              <DollarSign className="h-4 w-4 mr-1" />
                              <span className="font-semibold">{project.budget.toLocaleString()}</span>
                            </div>
                            {project.start_date && (
                              <div className="flex items-center text-slate-gray">
                                <Calendar className="h-4 w-4 mr-1" />
                                <span>{project.start_date}</span>
                              </div>
                            )}
                            <div className="text-slate-gray">
                              Client: <span className="font-semibold">{project.client_name}</span>
                            </div>
                            {(project.milestone_count > 0 || project.task_count > 0) && (
                              <div className="text-slate-gray text-xs">
                                {project.milestone_count || 0} milestones • {project.task_count || 0} tasks
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-3 flex-shrink-0">
                          <span className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${getStatusClass(project.status)}`}>
                            {formatStatus(project.status)}
                          </span>
                          {user?.role === 'admin' && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-8 w-8 p-0"
                                  onClick={(e) => e.preventDefault()}
                                  disabled={actionLoading === project.id}
                                  data-testid={`project-menu-${project.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-56">
                                {/* Move to Stage - submenu for desktop */}
                                <DropdownMenuSub>
                                  <DropdownMenuSubTrigger>
                                    <ArrowRight className="mr-2 h-4 w-4" />
                                    Move to Stage
                                  </DropdownMenuSubTrigger>
                                  <DropdownMenuSubContent side="left" className="w-56">
                                    {PIPELINE_STAGES.map((stage) => (
                                      <DropdownMenuItem 
                                        key={stage.key}
                                        onClick={(e) => handleMoveToStage(e, project.id, stage.key)}
                                        disabled={project.status === stage.key}
                                        className={project.status === stage.key ? 'bg-gray-100' : ''}
                                      >
                                        <span className="mr-2">{stage.icon}</span>
                                        {stage.label}
                                        {project.status === stage.key && <span className="ml-auto text-xs text-fearless-blue">✓</span>}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuSubContent>
                                </DropdownMenuSub>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem 
                                  onClick={(e) => handleComplete(e, project.id)}
                                  className="cursor-pointer"
                                  data-testid={`complete-project-${project.id}`}
                                >
                                  <CheckCircle className="h-4 w-4 mr-2 text-green-600" />
                                  <span>Completed</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleArchive(e, project.id)}
                                  className="cursor-pointer"
                                  data-testid={`archive-project-${project.id}`}
                                >
                                  <Archive className="h-4 w-4 mr-2 text-blue-600" />
                                  <span>Archive Project</span>
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={(e) => handleDelete(e, project.id)}
                                  className="cursor-pointer text-red-600 focus:text-red-600"
                                  data-testid={`delete-project-${project.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  <span>Delete Project</span>
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProjectsPage;
